// src/pages/company-admin/CompanyPartnersPage.tsx
import React, { useEffect, useState } from 'react';
import {
    listCompanyPartners,
    createCompanyPartner,
    updateCompanyPartner,
    deleteCompanyPartner,
    type CompanyPartner,
} from 'src/features/company/api';

import { type CompanyType, COMPANY_TYPE_OPTIONS } from '../../features/company/types';
import { useToast } from 'src/shared/ui/toast/useToast';

type Row = CompanyPartner & {
    editing?: boolean;
    editName?: string;
    editType?: CompanyType | '';
};

const CompanyPartnersPage: React.FC = () => {
    const toast = useToast();

    const [rows, setRows] = useState<Row[]>([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const [newName, setNewName] = useState('');
    const [newType, setNewType] = useState<CompanyType | ''>('');
    const [creating, setCreating] = useState(false);

    const [savingId, setSavingId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const reload = async () => {
        setLoading(true);
        setErr(null);
        try {
            const data = await listCompanyPartners();
            setRows(
                data.map((p) => ({
                    ...p,
                    editing: false,
                    editName: p.name,
                    editType: p.type ?? '',
                })),
            );
        } catch (e: any) {
            setErr(e?.response?.data?.error?.message ?? '파트너 회사 목록을 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void reload();
    }, []);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newName.trim()) {
            toast.warning('회사명을 입력해 주세요.', {
                title: '입력 필요',
            });
            return;
        }
        setCreating(true);
        setErr(null);
        try {
            const created = await createCompanyPartner({
                name: newName.trim(),
                type: newType || undefined,
            });
            setNewName('');
            setNewType('');
            setRows((prev) => [
                {
                    ...created,
                    editing: false,
                    editName: created.name,
                    editType: created.type ?? '',
                },
                ...prev,
            ]);
            toast.success(`${created.name} 회사를 추가했습니다.`, {
                title: '추가 완료',
            });
        } catch (e: any) {
            const msg = e?.response?.data?.error?.message ?? '파트너 회사 생성 중 오류가 발생했습니다.';
            setErr(msg);
            toast.error(msg, {
                title: '추가 실패',
            });
        } finally {
            setCreating(false);
        }
    };

    const startEdit = (id: string) => {
        setRows((prev) =>
            prev.map((r) => (r.id === id ? { ...r, editing: true, editName: r.name, editType: r.type ?? '' } : r)),
        );
    };

    const cancelEdit = (id: string) => {
        setRows((prev) =>
            prev.map((r) => (r.id === id ? { ...r, editing: false, editName: r.name, editType: r.type ?? '' } : r)),
        );
    };

    const saveEdit = async (id: string) => {
        const row = rows.find((r) => r.id === id);
        if (!row) return;
        const name = row.editName?.trim();
        if (!name) {
            // alert('회사명을 입력해 주세요.');
            toast.warning('회사명을 입력해 주세요.', {
                title: '입력 필요',
            });

            return;
        }
        setSavingId(id);
        try {
            const updated = await updateCompanyPartner(id, {
                name,
                type: row.editType || '',
            });
            setRows((prev) =>
                prev.map((r) =>
                    r.id === id
                        ? {
                              ...updated,
                              editing: false,
                              editName: updated.name,
                              editType: updated.type ?? '',
                          }
                        : r,
                ),
            );
            toast.success(`${updated.name} 정보를 수정했습니다.`, {
                title: '수정 완료',
            });
        } catch (e: any) {
            toast.error(e?.response?.data?.error?.message ?? '파트너 회사 수정 중 오류가 발생했습니다.', {
                title: '수정 실패',
            });
        } finally {
            setSavingId(null);
        }
    };

    const handleDelete = async (id: string) => {
        const target = rows.find((r) => r.id === id);

        if (!window.confirm('이 회사를 삭제하시겠습니까?\n이미 프로젝트에 사용 중인 회사는 삭제할 수 없습니다.')) {
            return;
        }
        setDeletingId(id);
        try {
            await deleteCompanyPartner(id);
            setRows((prev) => prev.filter((r) => r.id !== id));
            toast.success(`${target?.name ?? '회사'}를 삭제했습니다.`, {
                title: '삭제 완료',
            });
        } catch (e: any) {
            toast.error(e?.response?.data?.error?.message ?? '파트너 회사 삭제 중 오류가 발생했습니다.', {
                title: '삭제 실패',
            });
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="p-4 space-y-4">
            <div>
                <h2 className="text-sm font-semibold text-gray-900">Partner Companies</h2>
                <p className="mt-1 text-xs text-gray-500">
                    이 회사에서 사용하는 협력사 / 발주처 / PMC / QC / 공급사 등을 관리합니다.
                </p>
            </div>

            {err && (
                <div className="text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-md px-3 py-2">
                    {err}
                </div>
            )}

            {/* 생성 폼 */}
            <form
                onSubmit={handleCreate}
                className="flex flex-wrap items-end gap-2 border border-gray-200 rounded-xl px-3 py-3 bg-gray-50/60"
            >
                <div className="flex-1 min-w-[180px] space-y-1">
                    <label className="block text-xs font-medium text-gray-700">회사명</label>
                    <input
                        type="text"
                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-sky-500"
                        placeholder="예: Hyundai Steel"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        disabled={creating}
                    />
                </div>
                <div className="w-40 space-y-1">
                    <label className="block text-xs font-medium text-gray-700">유형</label>
                    <select
                        className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-sky-500"
                        value={newType}
                        onChange={(e) => setNewType(e.target.value as CompanyType | '')}
                        disabled={creating}
                    >
                        <option value="">선택 안 함</option>
                        {COMPANY_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex justify-end">
                    <button
                        type="submit"
                        className="px-3 py-1.5 rounded-full bg-sky-700 text-white text-xs hover:bg-sky-800 disabled:opacity-60"
                        disabled={creating}
                    >
                        {creating ? '추가 중…' : '회사 추가'}
                    </button>
                </div>
            </form>

            {/* 목록 */}
            <div className="border-t border-gray-200" />

            <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                    <thead>
                        <tr className="bg-gray-50 text-xs text-gray-500">
                            <th className="px-3 py-2 font-medium">회사명</th>
                            <th className="px-3 py-2 font-medium">유형</th>
                            <th className="px-3 py-2 font-medium">상태</th>
                            <th className="px-3 py-2 font-medium text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="text-xs">
                        {loading && (
                            <tr>
                                <td colSpan={4} className="px-3 py-4 text-center text-gray-400">
                                    로딩 중…
                                </td>
                            </tr>
                        )}

                        {!loading && rows.length === 0 && (
                            <tr>
                                <td colSpan={4} className="px-3 py-4 text-center text-gray-400">
                                    등록된 파트너 회사가 없습니다. 위 폼을 사용하여 회사를 추가해 주세요.
                                </td>
                            </tr>
                        )}

                        {!loading &&
                            rows.map((r) => (
                                <tr key={r.id} className="border-t">
                                    <td className="px-3 py-2">
                                        {r.editing ? (
                                            <input
                                                type="text"
                                                className="w-full border border-gray-300 rounded-md px-2 py-1 text-xs"
                                                value={r.editName ?? ''}
                                                onChange={(e) =>
                                                    setRows((prev) =>
                                                        prev.map((x) =>
                                                            x.id === r.id ? { ...x, editName: e.target.value } : x,
                                                        ),
                                                    )
                                                }
                                                disabled={savingId === r.id}
                                            />
                                        ) : (
                                            r.name
                                        )}
                                    </td>
                                    <td className="px-3 py-2">
                                        {r.editing ? (
                                            <select
                                                className="border border-gray-300 rounded-md px-2 py-1 text-xs bg-white"
                                                value={r.editType ?? ''}
                                                onChange={(e) =>
                                                    setRows((prev) =>
                                                        prev.map((x) =>
                                                            x.id === r.id
                                                                ? { ...x, editType: e.target.value as CompanyType | '' }
                                                                : x,
                                                        ),
                                                    )
                                                }
                                                disabled={savingId === r.id}
                                            >
                                                <option value="">선택 안 함</option>
                                                {COMPANY_TYPE_OPTIONS.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            (r.type ?? '-')
                                        )}
                                    </td>
                                    <td className="px-3 py-2">
                                        {r.status === 'ACTIVE' ? (
                                            <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                                                ACTIVE
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">
                                                {r.status}
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right space-x-2">
                                        {r.editing ? (
                                            <>
                                                <button
                                                    type="button"
                                                    className="text-xs text-gray-600 hover:underline disabled:opacity-60"
                                                    onClick={() => cancelEdit(r.id)}
                                                    disabled={savingId === r.id}
                                                >
                                                    취소
                                                </button>
                                                <button
                                                    type="button"
                                                    className="text-xs text-sky-700 hover:underline disabled:opacity-60"
                                                    onClick={() => saveEdit(r.id)}
                                                    disabled={savingId === r.id}
                                                >
                                                    저장
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    className="text-xs text-gray-600 hover:underline"
                                                    onClick={() => startEdit(r.id)}
                                                >
                                                    수정
                                                </button>
                                                <button
                                                    type="button"
                                                    className="text-xs text-rose-600 hover:underline disabled:opacity-60"
                                                    onClick={() => handleDelete(r.id)}
                                                    disabled={deletingId === r.id}
                                                >
                                                    삭제
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CompanyPartnersPage;
